import React, { useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Loader2, ShieldCheck, RefreshCw } from 'lucide-react';

// Wave 3.61 — Super Admin only dialog. Wraps
// POST /api/admin/ledger-backfill with dry-run and apply modes plus
// optional scoping (company / material / packet_no). Displays the
// full discrepancy report inline so the operator can review before
// running an --apply pass.

const MATERIAL_OPTIONS = [
  { value: '__all__', label: 'All materials' },
  { value: 'diamond', label: 'Diamond' },
  { value: 'color_stone', label: 'Color Stone' },
  { value: 'pearl', label: 'Pearl' },
];

export function LedgerBackfillDialog({ open, onOpenChange, companies = [] }) {
  const [companyId, setCompanyId] = useState('__all__');
  const [material, setMaterial] = useState('__all__');
  const [packetNo, setPacketNo] = useState('');
  const [applyChanges, setApplyChanges] = useState(false);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const companyOptions = useMemo(
    () => [
      { value: '__all__', label: 'All companies' },
      ...companies.map((c) => ({ value: c.id, label: c.name })),
    ],
    [companies],
  );

  const reset = () => {
    setCompanyId('__all__');
    setMaterial('__all__');
    setPacketNo('');
    setApplyChanges(false);
    setReport(null);
  };

  const runBackfill = async (forceApply) => {
    setBusy(true);
    setReport(null);
    try {
      const body = {
        company_id: companyId === '__all__' ? null : companyId,
        material: material === '__all__' ? null : material,
        packet_no: packetNo.trim() || null,
        apply: !!forceApply,
      };
      const { data } = await api.post('/admin/ledger-backfill', body);
      setReport(data);
      const s = data?.summary || {};
      if (forceApply) {
        if (s.rows_updated > 0 || s.packets_with_balance_diffs > 0) {
          toast.success(`Repaired ${s.rows_updated} ledger row(s) across ${s.packets_with_balance_diffs} packet balance(s).`);
        } else {
          toast.success('Nothing to repair — all packets already tally.');
        }
      } else {
        if ((s.packets_with_row_diffs || 0) + (s.packets_with_balance_diffs || 0) === 0) {
          toast.success('No discrepancies found. Ledgers tally cleanly.');
        } else {
          toast(`Dry-run: found ${s.packets_with_row_diffs} packet(s) with row diffs and ${s.packets_with_balance_diffs} with balance diffs.`, { icon: '⚠️' });
        }
      }
    } catch (e) {
      toast.error(extractError(e, 'Backfill failed'));
    } finally {
      setBusy(false);
    }
  };

  const onRunClick = () => {
    if (applyChanges) {
      setConfirmOpen(true);
    } else {
      runBackfill(false);
    }
  };

  const onConfirmApply = async () => {
    setConfirmOpen(false);
    await runBackfill(true);
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (!v) reset();
          onOpenChange(v);
        }}
      >
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="ledger-backfill-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck size={18} className="text-emerald-600" /> Ledger Tally Repair
            </DialogTitle>
            <DialogDescription>
              Recomputes running balances on every ledger row and reconciles each
              packet document against its ledger. Dry-run reports discrepancies
              without writing anything. Apply mode writes the repair to the
              database.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Company scope</Label>
              <Select value={companyId} onValueChange={setCompanyId}>
                <SelectTrigger data-testid="ledger-backfill-company"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {companyOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Material</Label>
              <Select value={material} onValueChange={setMaterial}>
                <SelectTrigger data-testid="ledger-backfill-material"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MATERIAL_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Packet Number (optional)</Label>
              <Input
                value={packetNo}
                onChange={(e) => setPacketNo(e.target.value)}
                placeholder="e.g. D-RB-004"
                data-testid="ledger-backfill-packet-no"
              />
            </div>

            <div className="col-span-2 flex items-center justify-between rounded-md border border-[var(--divider)] bg-[var(--paper-warm)] px-3 py-2 mt-1">
              <div className="flex items-center gap-2">
                <AlertTriangle
                  size={16}
                  className={applyChanges ? 'text-amber-600' : 'text-slate-400'}
                />
                <div>
                  <div className="text-sm font-medium text-slate-800">Apply changes</div>
                  <div className="text-[11px] text-slate-500">
                    Off = dry-run (safe, read-only preview). On = writes repair to database.
                  </div>
                </div>
              </div>
              <Switch
                checked={applyChanges}
                onCheckedChange={setApplyChanges}
                data-testid="ledger-backfill-apply-toggle"
              />
            </div>
          </div>

          {report && <ReportPanel report={report} />}

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="ledger-backfill-close">
              Close
            </Button>
            <Button
              onClick={onRunClick}
              disabled={busy}
              className="luxury-button"
              data-testid="ledger-backfill-run"
            >
              {busy ? (
                <><Loader2 size={14} className="mr-1.5 animate-spin" /> Running…</>
              ) : applyChanges ? (
                <><RefreshCw size={14} className="mr-1.5" /> Run &amp; Apply</>
              ) : (
                <><RefreshCw size={14} className="mr-1.5" /> Run Dry-Run</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md" data-testid="ledger-backfill-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-600" /> Confirm write to database
            </DialogTitle>
            <DialogDescription>
              This will rewrite ledger row balances and packet balance
              aggregates that don&apos;t already match the recomputed running
              balance. The operation is safe and idempotent, but it does write
              to the shared database. Run a dry-run first if you haven&apos;t.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="ledger-backfill-cancel-apply">
              Cancel
            </Button>
            <Button
              onClick={onConfirmApply}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="ledger-backfill-confirm-apply"
            >
              Yes, apply repair
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ReportPanel({ report }) {
  const summary = report?.summary || {};
  const materials = report?.materials || {};
  const packetsWithDiffs = [];
  for (const [mat, mr] of Object.entries(materials)) {
    for (const pkt of mr?.packets || []) {
      packetsWithDiffs.push({ material: mat, pkt });
    }
  }
  const clean =
    (summary.packets_with_row_diffs || 0) === 0 &&
    (summary.packets_with_balance_diffs || 0) === 0;
  return (
    <div className="mt-4 space-y-3" data-testid="ledger-backfill-report">
      <div className="flex items-center gap-2">
        {clean ? (
          <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle2 size={12} className="mr-1" /> All packets tally cleanly
          </Badge>
        ) : (
          <Badge className="bg-amber-50 text-amber-700 border border-amber-200">
            <AlertTriangle size={12} className="mr-1" /> Discrepancies found
          </Badge>
        )}
        <Badge variant="outline" className="text-xs">
          {report.apply_changes ? 'Applied' : 'Dry-run'}
        </Badge>
      </div>

      <Card className="p-3">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center">
          <SummaryStat label="Packets scanned" value={summary.packets_scanned} />
          <SummaryStat label="Row diffs" value={summary.packets_with_row_diffs} highlight={summary.packets_with_row_diffs > 0} />
          <SummaryStat label="Balance diffs" value={summary.packets_with_balance_diffs} highlight={summary.packets_with_balance_diffs > 0} />
          <SummaryStat label="Rows updated" value={summary.rows_updated} highlight={summary.rows_updated > 0} />
          <SummaryStat label="Unknown types" value={summary.unknown_row_types} highlight={summary.unknown_row_types > 0} />
        </div>
      </Card>

      {packetsWithDiffs.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <div className="ledger-table max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[var(--paper-warm)] z-10">
                <tr>
                  <th className="text-left px-2 py-1">Material</th>
                  <th className="text-left px-2 py-1">Packet</th>
                  <th className="text-right px-2 py-1">Balance ct (before → after)</th>
                  <th className="text-right px-2 py-1">Balance pcs (before → after)</th>
                  <th className="text-right px-2 py-1">Rows repaired</th>
                </tr>
              </thead>
              <tbody>
                {packetsWithDiffs.map(({ material, pkt }) => {
                  const before = pkt.packet_before || {};
                  const after = pkt.packet_after || {};
                  const changedCt = Number(before.balance_carats || 0) !== Number(after.balance_carats || 0);
                  const changedPc = Number(before.balance_pieces || 0) !== Number(after.balance_pieces || 0);
                  return (
                    <tr key={`${material}-${pkt.packet_id}`}>
                      <td className="px-2 py-1 font-mono text-[11px]">{material}</td>
                      <td className="px-2 py-1 font-mono">{pkt.packet_no || pkt.packet_id?.slice(0, 8)}</td>
                      <td className={`px-2 py-1 text-right font-mono tabular-nums ${changedCt ? 'text-amber-700 font-medium' : 'text-slate-500'}`}>
                        {fmtCt(before.balance_carats)} → {fmtCt(after.balance_carats)}
                      </td>
                      <td className={`px-2 py-1 text-right font-mono tabular-nums ${changedPc ? 'text-amber-700 font-medium' : 'text-slate-500'}`}>
                        {before.balance_pieces ?? 0} → {after.balance_pieces ?? 0}
                      </td>
                      <td className="px-2 py-1 text-right font-mono tabular-nums">{(pkt.row_updates || []).length}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function SummaryStat({ label, value, highlight }) {
  return (
    <div className="rounded-md border border-[var(--divider)] px-2 py-1">
      <div className={`text-lg font-semibold tabular-nums ${highlight ? 'text-amber-700' : 'text-slate-800'}`}>{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
    </div>
  );
}

function fmtCt(v) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  return Number(v).toFixed(3);
}
